import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Paper,
    Radio,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import {generateUUID} from '../../../../../utility/uuidUtil';

const BoxGrid = ({
                     index,
                     componentId,
                     value,
                     onChange,
                     descriptionHtml,
                     onDescriptionHtmlChange,
                     commitDescriptionHtml,
                     isEditMode,
                     enableValidation,
                     isFormArchived,
                     rows,
                     columns,
                     readOnly,
                     type,
                     placeholderValues,
                     accessLevel,
                     error,
                     audience,
                     recipientType,
                     linkedToStudents,
                     submitMode,
                     allowCustomRows
                 }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const customRows = Array.isArray(value?.__customRows) ? value.__customRows : [];
    const allRows = [...(rows || []), ...customRows];

    const updateGridValue = (updater) => {
        const nextValue = updater({...((value && typeof value === 'object') ? value : {})});
        onChange({
            id: componentId,
            value: nextValue
        });
    };

    const addCustomRow = () => {
        updateGridValue((current) => {
            const nextCustomRows = [
                ...(Array.isArray(current.__customRows) ? current.__customRows : []),
                {id: generateUUID(), title: ''}
            ];
            return {
                ...current,
                __customRows: nextCustomRows
            };
        });
    };

    const updateCustomRowTitle = (rowId, title) => {
        updateGridValue((current) => ({
            ...current,
            __customRows: (Array.isArray(current.__customRows) ? current.__customRows : [])
                .map((row) => row.id === rowId ? {...row, title} : row)
        }));
    };

    const removeCustomRow = (rowId) => {
        updateGridValue((current) => {
            const next = {
                ...current,
                __customRows: (Array.isArray(current.__customRows) ? current.__customRows : [])
                    .filter((row) => row.id !== rowId)
            };
            delete next[rowId];
            return next;
        });
    };

    const renderRowTitleCell = (row, rowIndex) => {
        const isCustomRow = customRows.some((customRow) => customRow.id === row.id);

        if (!isCustomRow) {
            return (
                <Typography sx={{...theme.typography.body1, color: theme.palette.text.primary}}>
                    {row.title}
                </Typography>
            );
        }

        return (
            <Box display="flex" alignItems="center" gap={1}>
                <TextField
                    fullWidth
                    size="small"
                    value={row.title || ''}
                    onChange={(e) => updateCustomRowTitle(row.id, e.target.value)}
                    placeholder={`Row ${rowIndex + 1}`}
                    disabled={readOnly || !submitMode}
                />
                {!readOnly && submitMode && (
                    <IconButton onClick={() => removeCustomRow(row.id)}>
                        <DeleteIcon/>
                    </IconButton>
                )}
            </Box>
        );
    };

    const handleToggle = (row, column) => {
        updateGridValue((updatedSelections) => {
            if (!updatedSelections[row.id]) {
                updatedSelections[row.id] = [];
            }

            if (updatedSelections[row.id].includes(column.id)) {
                updatedSelections[row.id] = updatedSelections[row.id].filter(item => item !== column.id);
            } else {
                updatedSelections[row.id].push(column.id);
            }
            return updatedSelections;
        });
    };

    const handleToggleRadio = (row, column) => {
        updateGridValue((updatedSelections) => {
            if (updatedSelections[row.id] === column.id) {
                delete updatedSelections[row.id];
            } else {
                updatedSelections[row.id] = column.id;
            }
            return updatedSelections;
        });
    };

    const isChecked = (row, column) =>
        !!value && Array.isArray(value[row.id]) && value[row.id].includes(column.id);

    const isCheckedRadio = (row, column) =>
        !!value && value[row.id] === column.id;

    const buildEditableList = (data, listKey) => {
        const count = data.length;

        return (
            <>
                {data.map((el, optIndex) => (
                    <Box
                        key={optIndex}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="90%"
                    >
                        <Box display="flex" alignItems="center" gap={1} width="90%">
                            <Typography>{optIndex + 1}.</Typography>
                            <TextField
                                variant="standard"
                                value={el.title}
                                fullWidth
                                disabled={isFormArchived}
                                onChange={e =>
                                    onChange({
                                        index,
                                        option: e.target.value,
                                        optionId: optIndex,
                                        listKey
                                    })
                                }
                            />
                        </Box>
                        {count > 1 && (
                            <IconButton size="small">
                                <DeleteIcon
                                    onClick={() =>
                                        isFormArchived
                                            ? {}
                                            : onChange({
                                                index,
                                                isRemove: true,
                                                optionId: optIndex,
                                                listKey
                                            })
                                    }
                                    disabled={isFormArchived}
                                />
                            </IconButton>
                        )}
                    </Box>
                ))}
                <Button
                    onClick={() => onChange({
                        index,
                        isAdd: true,
                        listKey
                    })}
                    startIcon={<AddIcon/>}
                    cursor="pointer"
                    disabled={isFormArchived}
                >
                    Add option
                </Button>
            </>
        );
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <RichEditor
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                        <Box display="flex" justifyContent="space-between" marginTop={2}>
                            <Box width="100%">
                                <Typography fontWeight="bold">Rows</Typography>
                                {buildEditableList(rows, 'rows')}
                            </Box>
                            <Box width="100%">
                                <Typography fontWeight="bold">Columns</Typography>
                                {buildEditableList(columns, 'columns')}
                            </Box>
                        </Box>
                        <Box display="flex" flexDirection="column">
                            <RequiredCheckbox
                                enableValidation={enableValidation}
                                index={index}
                                onChange={onChange}
                                isFormArchived={isFormArchived}
                            />
                            <FormControlLabel
                                sx={{height: '40px'}}
                                control={
                                    <Checkbox
                                        disabled={isFormArchived}
                                        checked={Boolean(allowCustomRows)}
                                        onChange={(e) => onChange({
                                            index,
                                            allowCustomRows: e.target.checked
                                        })}
                                    />
                                }
                                label="Allow custom rows while submitting"
                            />
                        </Box>
                    </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            error={!!error}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            submitMode={submitMode}
                        />
                        <FormControl variant="standard" error={!!error} fullWidth>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell/>
                                            {columns.map((column, colIndex) => (
                                                <TableCell
                                                    key={colIndex}
                                                    align="center"
                                                    sx={{
                                                        ...theme.typography.body1,
                                                        color: theme.palette.text.primary
                                                    }}
                                                >
                                                    {column.title}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {allRows.map((row, rowIndex) => (
                                            <TableRow key={rowIndex}>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    sx={{
                                                        ...theme.typography.body1,
                                                        color: theme.palette.text.primary
                                                    }}
                                                >
                                                    {renderRowTitleCell(row, rowIndex)}
                                                </TableCell>
                                                {columns.map((column, columnIndex) => (
                                                    <TableCell key={columnIndex} align="center">
                                                        {type === 'tickBoxGrid'
                                                            ? (
                                                                <Checkbox
                                                                    disabled={readOnly}
                                                                    checked={isChecked(row, column)}
                                                                    onChange={() => handleToggle(row, column)}
                                                                    size="small"
                                                                />
                                                            )
                                                            : (
                                                                <Radio
                                                                    disabled={readOnly}
                                                                    size="small"
                                                                    key={`radioButton-${rowIndex}-${columnIndex}-${value && value[row.id] ? value[row.id] : ''}`}
                                                                    checked={isCheckedRadio(row, column)}
                                                                    onClick={() => handleToggleRadio(row, column)}
                                                                />
                                                            )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {Boolean(allowCustomRows) && submitMode && !readOnly && (
                                <Button
                                    onClick={addCustomRow}
                                    startIcon={<AddIcon/>}
                                    sx={{mt: 2, alignSelf: 'flex-start'}}
                                >
                                    Add row
                                </Button>
                            )}
                            <FormHelperText>{error}</FormHelperText>
                        </FormControl>
                    </>
                )}
        </Box>
    );
};

BoxGrid.propTypes = {
    index: PropTypes.number,
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.object,
    rows: PropTypes.array,
    columns: PropTypes.array,
    onChange: PropTypes.func,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    type: PropTypes.string,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    submitMode: PropTypes.bool,
    allowCustomRows: PropTypes.bool
};

export default BoxGrid;